import * as CertificationWatermark from '@tableau/certification-watermark';
import { CheckBoxWidget, DialogPositioningBehavior, DialogType, DialogWidget, HorizontalOriginOption, TabStyles, VerticalOriginOption } from '@tableau/widgets';
import { BodyCell, DataGrid, HeaderColumn } from '@tableau/widgets-datagrid';
import * as React from 'react';
import * as _ from 'underscore';

import { DataGridProfilePicture } from '../../../../app/content/DataGridProfilePicture';
import { Hover } from '../../../../app/spaces/Components/Hover';
import { TooltipAnchor } from '../../../../app/spaces/Tooltips/TooltipAnchor';
import { SiteRolePopover } from '../../../../app/users/SiteRolePopover';
import { Formatters, Messages } from '../../../../codegen/Localize';
import { cssClass, textEllipsis } from '../../../../style/index';
import { AngularContext } from '../../../../ts/factories/AngularContextFactory';
import { IScheduleFrequencyDescriptionFilter } from '../../../../ts/filters/ScheduleFrequencyDescriptionFilter';
import { SizeFilter } from '../../../../ts/filters/SizeFilter';
import { L10n } from '../../../../ts/L10n/L10n';
import { Set } from '../../../../ts/models/Set';
import { SiteRoleOptionsResource } from '../../../../ts/resources/SiteRoleOptions';
import { DefinedBy, ISchedule } from '../../../../ts/ServerApi/types/ISchedule';
import { ISiteNameWithId } from '../../../../ts/ServerApi/types/ISiteNameWithId';
import { Duration } from '../../../utilities/Duration';
import { imgUrl } from '../../../utilities/ImgUrl';
import { CommonStyles } from '../../CommonStyles';
import { IReactTranslateService } from '../../IReactTranslateService';
import { AngularDirective } from '../AngularDirective';
import { FavoriteToggle } from '../FavoriteToggle/FavoriteToggle';
import { InterpolatedString } from '../InterpolatedString';
import { DeprecatedReactMenu } from '../Menu';
import { ReactCheckboxTristate } from '../ReactCheckboxTristate';
import { SiteRolePickerMenu } from '../SiteRolePickerMenu';
import { CellWithCountAndToolTip } from './CellWithCount';
import { StyledCellFormatter } from './StyledCellWrapper';

export module DataGridCells {

  export interface CellFormatterWithStyle {
    cellFormatter: React.Factory<BodyCell.Props>;
    cellChildStyle: React.CSSProperties;
  }

  export function CompositeCell(
    cellFormatters: React.Factory<BodyCell.Props>[],
    cellWrapperStyle: React.CSSProperties,
    cellChildStyle: React.CSSProperties
  ): React.StatelessComponent<BodyCell.Props> {

    const cellFormattersWithStyle: CellFormatterWithStyle[] = cellFormatters.map(cellFormatter => ({ cellFormatter, cellChildStyle }));

    return CompositeCellWithCustomChildStyles(cellFormattersWithStyle, cellWrapperStyle);
  }

  export function CompositeCellWithCustomChildStyles(
    cellFormattersWithStyle: CellFormatterWithStyle[],
    cellWrapperStyles: React.CSSProperties,
  ): React.StatelessComponent<BodyCell.Props> {
    return function CompositeCellWithCustomChildStyles(props: BodyCell.Props) {
      return (
        <span style={cellWrapperStyles}>
          {cellFormattersWithStyle.map((cell, index) =>
            <span style={cell.cellChildStyle} key={index + props.rowIdx}>
              {React.createElement(cell.cellFormatter, props)}
            </span>
          )}
        </span>
      );
    };
  }

  export function CertificationMarkTooltipCell(
    isCertified: (row: DataGrid.Row) => boolean,
    getTooltipProps: (row: DataGrid.Row) => CertificationWatermark.CertificationTooltipProps
  ): React.Factory<BodyCell.Props> {
    return React.createFactory(class extends React.Component<BodyCell.Props, any> {
      displayName: 'CertificationMarkTooltipCell';
      render() {
        if (isCertified(this.props.row)) {
          const tooltipProps = getTooltipProps(this.props.row);
          return <CertificationWatermark.CertificationTooltipComponent {...tooltipProps} />;
        } else {
          return <span></span>;
        }
      }
    });
  }

  export function CertificationMarkIconCell(
    isCertified: (row: DataGrid.Row) => boolean,
    getIconProps: (row: DataGrid.Row) => CertificationWatermark.CertificationIconProps & React.HTMLProps<HTMLImageElement>
  ): React.Factory<BodyCell.Props> {
    return React.createFactory(class extends React.Component<BodyCell.Props, any> {
      displayName: 'CertificationMarkIconCell';
      render() {
        if (isCertified(this.props.row)) {
          const iconProps = getIconProps(this.props.row);
          return <CertificationWatermark.CertificationIconComponent {...iconProps} />;
        } else {
          return <span></span>;
        }
      }
    });
  }

  export interface ICheckboxCellOptions {
    onCheckboxClicked: (row: DataGrid.Row) => void;
    checkboxCellTestIdFn?: (row: DataGrid.Row) => string;
    isRowSelected: (rowKey: string) => boolean;
  }

  export function CheckboxCell(options: ICheckboxCellOptions): React.Factory<BodyCell.Props> {
    return StyledCellFormatter(
      React.createFactory(class extends React.Component<BodyCell.Props, any> {
        displayName: 'CheckboxCell';

        boundHandleClick = this.handleClick.bind(this);
        handleClick(event: React.MouseEvent<HTMLSpanElement>) {
          const props: BodyCell.Props = this.props;
          options.onCheckboxClicked(props.row);
          event.preventDefault();
          event.stopPropagation();
        }
        render() {
          const props: BodyCell.Props = this.props;
          const classes = ['tb-checkbox'];
          if (options.isRowSelected(props.row.key)) {
            classes.push('tb-checked');
          }

          return (
            <span className="tb-checkbox-cell"
              onClick={this.boundHandleClick}
              data-tb-test-id={options.checkboxCellTestIdFn && options.checkboxCellTestIdFn(props.row)}>
              <span className={classes.join(' ')} />
            </span>
          );
        }
      })
    );
  }

  export interface IFavoriteCellOptions {
    onAddFavorite: (row: DataGrid.Row) => void;
    onRemoveFavorite: (row: DataGrid.Row) => void;
    favoriteCellTestId?: string;
    isFavoritable?: (row: DataGrid.Row) => boolean;
  }

  export function FavoriteCell(options: IFavoriteCellOptions): React.StatelessComponent<BodyCell.Props> {
    return React.createFactory(class extends React.Component<BodyCell.Props, any> {
      displayName: 'FavoriteCell';

      private boundAddFavorite = () => options.onAddFavorite(this.props.row);
      private boundRemoveFavorite = () => options.onRemoveFavorite(this.props.row);

      public render(): JSX.Element {
        if (_.isFunction(options.isFavoritable) && options.isFavoritable(this.props.row) === false) {
          return <span className="tb-favorite-button-cell" data-tb-test-id="tb-favorite-unavailable" />;
        }

        return (
          <span className="tb-favorite-button-cell"
            data-tb-test-id={options.favoriteCellTestId}>
            <FavoriteToggle isFavorite={this.props.row.data.favorite}
              onAddFavorite={this.boundAddFavorite}
              onRemoveFavorite={this.boundRemoveFavorite}
              canHaveKeyboardFocus={false} />
          </span>
        );
      }
    });
  }

  export function ProfilePictureCell(
    getProps: (row: DataGrid.Row) => DataGridProfilePicture.Props
  ): React.Factory<BodyCell.Props> {
    return React.createFactory(class extends React.Component<BodyCell.Props, any> {
      displayName: 'ProfilePictureCell';
      render() {
        const dataGridProfilePictureProps = getProps(this.props.row);
        return <DataGridProfilePicture {...dataGridProfilePictureProps} />;
      }
    });
  }

  export interface ISelectedItemsScope<T> extends ng.IScope {
    selectedItems: Set<T>;
  }

  export function SiteRoleCell(propertyName: string, translate: IReactTranslateService, siteRoleOptionsResource: SiteRoleOptionsResource,
    testId?: string): React.StatelessComponent<BodyCell.Props> {
    return PlainTextCellWithTransform(propertyName, siteRoleOptionsResource.siteRoleTransform(translate), testId);
  }

  export interface IDateWithOptionalIcon {
    dateText: string;
    iconClassName?: string;
    iconTitle: string;
  }

  export function DateTimeWithOpitonalLink(
    linkFn: (row: DataGrid.Row, propertyName: string, nullDateTimeLabel?: string) => IDateWithOptionalIcon,
    propertyName: string,
    nullDateTimeLabel?: string,
    testId?: string)
    : React.StatelessComponent<BodyCell.Props> {
    const iconStyle = { flex: 'none' };

    return StyledCellFormatter(
      function LinkCell(props: BodyCell.Props) {
        const data = linkFn(props.row, propertyName, nullDateTimeLabel);
        if (data.iconClassName) {
          return (
            <span data-tb-test-id={testId}
              style={CommonStyles.Layout.FlexBlockLeftAndCenter}>
              <span className={data.iconClassName} title={data.iconTitle} ></span>
              <span>{data.dateText}</span>
            </span>
          );
        }
        else {
          return <span data-tb-test-id={testId}>{data.dateText}</span>;
        }
      }
    );
  }

  export interface ILink {
    text: string;
    title?: string;
    url: string;
    iconClassName?: string;
    iconTitle?: string;
    testId?: string;
    style?: any;
    hideTooltips?: boolean;
  }

  export function Link(linkFn: (row: DataGrid.Row) => ILink): React.StatelessComponent<BodyCell.Props> {
    const iconStyle = { flex: 'none' };

    return function LinkCell(props: BodyCell.Props) {
      const link = linkFn(props.row);

      const textEllipsis = CommonStyles.Layout.TextEllipsis;

      const Link = () => (
        <a href={link.url}
          title={link.hideTooltips ? null : (link.title || link.text)}
          style={_.extend({ display: 'block' }, textEllipsis, link.style)}>
          {link.text}
        </a>
      );

      let Icon = null;

      if (link.iconClassName || link.iconTitle) {
        Icon = () => <span data-tb-test-id="data-grid-cell-icon"
          className={link.iconClassName}
          title={link.hideTooltips ? null : link.iconTitle}
          style={iconStyle} />;
      }

      return (
        <span data-tb-test-id={link.testId}
          style={CommonStyles.Layout.FlexBlockLeftAndCenter}>
          {Icon && <Icon />}
          <Link />
        </span>
      );
    };
  }

  export function LinkWithCount(linkFn: (row: DataGrid.Row) => ILink,
    count?: string,
    getPopoverContent?: (props: BodyCell.Props) => Promise<JSX.Element>
  ): React.StatelessComponent<BodyCell.Props> {
    return function LinkWithCountCell(props: BodyCell.Props) {
      const link = linkFn(props.row);
      const iconStyle = { flexShrink: 0 };
      const textStyles = _.extend({ display: 'block' }, CommonStyles.Layout.TextEllipsis, link.style);
      let Icon = null;

      if (link.iconClassName || link.iconTitle) {
        Icon = () => <span data-tb-test-id="data-grid-cell-icon"
          style={iconStyle}
          className={link.iconClassName}
          title={link.hideTooltips ? null : link.iconTitle} />;
      }

      const Link = () => (
        <a href={link.url}
          title={link.hideTooltips ? null : (link.title || link.text)}
          style={textStyles}>
          {link.text}
        </a>
      );

      const getContent = () => getPopoverContent(props);
      return (
        <CellWithCountAndToolTip count={count} testId={link.testId} getPopoverContent={getContent}>
          {Icon && <Icon />}
          <Link />
        </CellWithCountAndToolTip>
      );
    };
  }

  export function FixedValue(value: string): React.StatelessComponent<any> {
    return function FixedValueCell() {
      return <span title={value}>{value}</span>;
    };
  }

  export function PlainTextCell(propertyName: string, testId?: string): React.StatelessComponent<BodyCell.Props> {
    return function PlainTextCell(props: BodyCell.Props) {
      const text = props.row.data[propertyName];
      return <span title={text} data-tb-test-id={testId}>{text}</span>;
    };
  }

  export function PlainTextCellWithTransform(propertyName: string, transform: (value: any) => string,
    testId?: string): React.StatelessComponent<BodyCell.Props> {
    return StyledCellFormatter(
      function PlainTextCellWithTransform(props: BodyCell.Props) {
        const text = transform(props.row.data[propertyName]);
        return <span title={text} data-tb-test-id={testId}>{text}</span>;
      }
    );
  }

  export interface ITextWithIcon {
    text: string;
    title?: string;
    textStyle?: React.CSSProperties;
    iconClass: string;
    iconTitle?: string;
    hideTooltips?: boolean;
  }

  export function PlainTextCellWithIcon(metadataFn: (rowData: any) => ITextWithIcon,
    testId?: string): React.StatelessComponent<BodyCell.Props> {
    return StyledCellFormatter(
      function PlainTextCellWithIcon(props) {
        const data = metadataFn(props.row.data);

        return (
          <span data-tb-test-id={testId || 'plain-text-cell-icon'}>
            <span data-tb-test-id="data-grid-cell-icon"
              className={data.iconClass}
              title={data.hideTooltips ? null : data.iconTitle} />
            <span data-tb-test-id="data-grid-cell-text"
              title={data.hideTooltips ? null : (data.title || data.text)}>
              {data.text}
            </span>
          </span>
        );
      }
    );
  }

  export interface IInteractiveIconTooltip {
    text: string;
    title: string;
    iconClass?: string;
    tooltipText?: string;
    tooltipReplacements?: any;
    tooltipLink?: string;
    tooltipLinkText?: string;
  }

  export interface InteractiveIconTooltipState {
    isDialogOpen: boolean;
  }

  export function InteractiveIconTooltipCell(
    getProps: (row: DataGrid.Row) => IInteractiveIconTooltip,
    translate: IReactTranslateService
  ): React.Factory<BodyCell.Props> {
    return React.createFactory(class extends React.Component<BodyCell.Props, InteractiveIconTooltipState> {
      private iconAnchorElement: HTMLElement;

      constructor(props: any) {
        super(props);
        this.state = { isDialogOpen: false };
      }

      hideDialog = () => {
        this.setState({ isDialogOpen: false });
      }

      showDialog = () => {
        this.setState({ isDialogOpen: true });
      }

      setAnchorElement = (el: HTMLElement) => {
        this.iconAnchorElement = el;
      }

      render() {
        const props = getProps(this.props.row);
        const link = {
          LINK: () =>
            <a
              data-tb-test-id={'cell-icon-hover-link'}
              className={'tb-data-grid-dialog-tooltip-link'}
              href={props.tooltipLink}
              style={{ textDecoration: 'none', color: TabStyles.Colors.Discrete, opacity: .80 }}
            >
              {props.tooltipLinkText}
            </a>
        };

        return (
          <span data-tb-test-id={'plain-text-cell-icon'}>
            <Hover
              groupName="plainTextCellWithLinkInTooltipIcon"
              onEnter={this.showDialog}
              onLeave={this.hideDialog}
              leaveOnScroll={true}
              className={this.state.isDialogOpen ? props.iconClass + '-active' : props.iconClass}
              enterDelay={0}
              useDocument={false}
            >
              <div ref={this.setAnchorElement} >
                {this.state.isDialogOpen && <DialogWidget
                  testId={'interactive-tooltip'}
                  type={DialogType.Uber}
                  showCloseButton={false}
                  onCloseRequest={this.hideDialog}
                  positioningBehavior={DialogPositioningBehavior.Anchored}
                  anchorEl={this.iconAnchorElement}
                  anchorOrigin={{ horizontal: HorizontalOriginOption.centerX, vertical: VerticalOriginOption.bottom }}
                  stackedOrigin={{ horizontal: HorizontalOriginOption.left, vertical: VerticalOriginOption.top }}
                  isModal={false}
                  showFooter={false}
                  dialogWidth={228}
                  dialogStyle={{ marginTop: CommonStyles.Sizes.NormalIconSize }}
                  dialogContentStyle={{ padding: CommonStyles.Sizes.MediumSpace }}
                  dialogBodyStyle={{ maxWidth: '228px' }}
                  title={props.title}
                >
                  <div style={{ color: CommonStyles.Colors.Black }}>
                    <InterpolatedString
                      baseStringKey={props.tooltipText}
                      translate={translate}
                      replacements={{ ...props.tooltipReplacements, ...link }}
                    />
                  </div>
                </DialogWidget>}
              </div>
            </Hover>
            <span data-tb-test-id="data-grid-cell-text">
              {props.text}
            </span>
          </span>
        );
      }
    });
  }

  const dataGridCellText = cssClass('plainTextCellWithCount', 'text', textEllipsis, {
    display: 'block'
  });

  function CreateCellText(text: string): JSX.Element {
    return (
      <span data-tb-test-id="data-grid-cell-text"
        className={dataGridCellText}
        title={text}>
        {text}
      </span>
    );
  }

  export function PlainTextCellWithCount(text: string,
    getPopoverContent?: (props: BodyCell.Props) => Promise<JSX.Element>,
    count?: string,
    testId?: string): React.StatelessComponent<BodyCell.Props> {

    return function PlainTextCellWithCount(props) {

      const getContent = () => getPopoverContent(props);
      return (
        <CellWithCountAndToolTip count={count} testId={testId} getPopoverContent={getContent}>
          {CreateCellText(text)}
        </CellWithCountAndToolTip>
      );
    };
  }

  export function PlainTextCellWithIconAndCount(metadataFn: (rowData: any) => ITextWithIcon,
    getPopoverContent?: (props: BodyCell.Props) => Promise<JSX.Element>,
    count?: string,
    testId?: string
  ): React.StatelessComponent<BodyCell.Props> {
    return StyledCellFormatter(
      function PlainTextCellWithIconAndCount(props) {
        const data = metadataFn(props.row.data);
        const iconStyle = { flexShrink: 0 };
        const textStyles = _.extend({ display: 'block' }, CommonStyles.Layout.TextEllipsis, data.textStyle);

        const Icon = () => <span data-tb-test-id="data-grid-cell-icon"
          style={iconStyle}
          className={data.iconClass}
          title={data.hideTooltips ? null : data.iconTitle} />;

        const Text = () => (
          <span data-tb-test-id="data-grid-cell-text"
            style={textStyles}
            title={data.hideTooltips ? null : (data.title || data.text)}>
            {data.text}
          </span>
        );

        const getContent = () => getPopoverContent(props);
        return (
          <CellWithCountAndToolTip count={count} testId={testId} getPopoverContent={getContent}>
            <Icon />
            <Text />
          </CellWithCountAndToolTip>
        );
      }
    );
  }

  export function DurationCell(
    startTimePropertyName: string,
    endTimePropertyName: string,
    nullDateTimeLabel?: string,
    testId?: string): React.StatelessComponent<BodyCell.Props> {
    return StyledCellFormatter(
      function DurationCell(props) {
        const startDateTime = props.row.data[startTimePropertyName];
        const endDateTime = props.row.data[endTimePropertyName];
        const duration = new Duration(startDateTime, endDateTime);
        const minimumTwoDigits = Intl.NumberFormat('en-US', { minimumIntegerDigits: 2 });
        if (startDateTime == null || endDateTime == null || duration.isNegative) {
          return nullDateTimeLabel == null ? null : <span data-tb-test-id={testId}>{nullDateTimeLabel}</span>;
        }
        return (
          <TooltipAnchor
            width={250}
            delay={0}
            title={Messages.flowRunHistoryColumn_duration()}
            tooltipContent={() => {
              return (
                <div>
                  <a href="" />
                  <div className={'tb-content-item-popover-title'}>
                    {Messages.flowRunHistoryColumn_duration_title()}
                  </div>
                  <div className={'tb-content-item-popover-description'}>
                    {duration.hours > 0 ?
                      Messages.flowRunHistoryColumn_duration_message_hoursMinutesSeconds({
                        HOURS_COUNT: duration.hours,
                        HOURS_LOC: Formatters.formatCount(duration.hours),
                        MINUTES_COUNT: duration.minutes,
                        MINUTES_LOC: Formatters.formatCount(duration.minutes),
                        SECONDS_COUNT: duration.seconds,
                        SECONDS_LOC: Formatters.formatCount(duration.seconds),
                      }) : duration.minutes > 0 ?
                        Messages.flowRunHistoryColumn_duration_message_minutesSeconds({
                          MINUTES_COUNT: duration.minutes,
                          MINUTES_LOC: Formatters.formatCount(duration.minutes),
                          SECONDS_COUNT: duration.seconds,
                          SECONDS_LOC: Formatters.formatCount(duration.seconds),
                        }) :
                        Messages.flowRunHistoryColumn_duration_message_seconds({
                          SECONDS_COUNT: duration.seconds,
                          SECONDS_LOC: Formatters.formatCount(duration.seconds),
                        })
                    }
                  </div>
                </div>
              );
            }
            }
          >
            <div data-tb-test-id={testId} style={CommonStyles.Layout.TextEllipsis}>
              {
                minimumTwoDigits.format(duration.hours) + ':' +
                minimumTwoDigits.format(duration.minutes) + ':' +
                minimumTwoDigits.format(duration.seconds)
              }
            </div>
          </TooltipAnchor>
        );
      }
    );
  }

  export function DateTimeCell(propertyName: string, nullDateTimeLabel?: string,
    testId?: string): React.StatelessComponent<BodyCell.Props> {
    return StyledCellFormatter(
      function DateTimeCell(props) {
        const dateTime = props.row.data[propertyName];
        const text = dateTime ? L10n.localizedDateTime(dateTime) : nullDateTimeLabel;
        return <span data-tb-test-id={testId}>{text}</span>;
      }
    );
  }

  export function OverridableDateTimeCell(dateProperty: string, overrideProperty: string,
    overrideLabel: string, testId?: string): React.StatelessComponent<BodyCell.Props> {
    return StyledCellFormatter(
      function OverridableDateTimeCell(props) {
        const override = props.row.data[overrideProperty];
        const dateTime = props.row.data[dateProperty];
        // show the overrideLabel if the override is true no matter the value of dateTime
        const text = !override && dateTime ? L10n.localizedDateTime(dateTime) : overrideLabel;
        return <span data-tb-test-id={testId}>{text}</span>;
      }
    );
  }

  export function SizeCell(propertyName: string,
    testId?: string): React.StatelessComponent<BodyCell.Props> {
    return StyledCellFormatter(
      function SizeCell(props: BodyCell.Props) {
        const sizeInBytes = props.row.data[propertyName];
        return <span data-tb-test-id={testId}>{SizeFilter(sizeInBytes)}</span>;
      }
    );
  }

  export function Checkmark(propertyName: string): React.StatelessComponent<BodyCell.Props> {
    const propertyNameParts = propertyName.split('.');
    return function CheckmarkCell(props: BodyCell.Props) {
      let value = props.row.data;
      _.each(propertyNameParts, (part: string) => {
        value = value[part];
      });

      return <span className={value ? 'tb-data-grid-icon tb-icon-checkmark' : null} />;
    };
  }


  export function LocalizedCount(propertyName: string, testId?: string): React.StatelessComponent<BodyCell.Props> {
    return function LocalizedCountCell(props: BodyCell.Props) {
      const count = props.row.data[propertyName];
      const localizedCount = L10n.localizedCount(count);
      return <span data-tb-test-id={testId}>{_.isNumber(count) ? localizedCount : ''}</span>;
    };
  }

  /**
   * Renders a cell containing a number with a horizontal bar, the width of which is proportional
   * to the number's magnitude. This effectively turns the entire column into a bar graph. The
   * cell's value is returned by dataValueFn, and the bar sizes are normalized to the return value
   * of maxValueFn. For example, if dataValueFn returns 5 and maxValueFn returns 10, the cell will
   * display 5 and the bar will occupy 50% of the column's width. maxValueFn should return the same
   * value for every row in the grid. If the cell value is greater than 0, then the minimum size of the bar
   * will be 2 pixel.
   *
   * @param dataValueFn A function that returns the cell's data value from the row's data object
   * @param maxValueFn A function that returns the maximum value of all the items from the row's data object
   * @param barElementTestId An optional test ID to override the default.
   * @returns {Factory<BodyCell.Props>}
   * @constructor
   */
  export function LocalizedCountWithBar(
    dataValueFn: (data: any) => number,
    maxValueFn: (data: any) => number,
    barElementTestId?: string): React.StatelessComponent<BodyCell.Props> {
    return function LocalizedCountCellWithBar(props: BodyCell.Props) {
      const count = dataValueFn(props.row.data);
      const max = maxValueFn(props.row.data);

      let displayBarLength: number;
      if (!_.isNumber(max) || max <= 0 || !_.isNumber(count) || count <= 0) {
        displayBarLength = 0;
      } else {
        displayBarLength = (count / max);
      }
      const minBarLength = count > 0 && displayBarLength > 0 ? '2px' : '0px';
      const barVerticalMargin = CommonStyles.Sizes.DefaultSpace;
      const barStyle: React.CSSProperties = {
        marginTop: barVerticalMargin,
        height: CommonStyles.Sizes.CellHeight - (2 * barVerticalMargin),
        width: (displayBarLength * 100) + '%',
        minWidth: minBarLength,
        backgroundColor: props.isSelected ?
          CommonStyles.Colors.Grey245 : CommonStyles.Colors.Grey218,
      };

      const textContainerStyle: React.CSSProperties = {
        lineHeight: CommonStyles.Sizes.CellHeight - (2 * barVerticalMargin) + 'px',
        paddingLeft: CommonStyles.Sizes.DefaultSpace,
        verticalAlign: 'top'
      };

      return (
        <div data-tb-test-id={barElementTestId || 'metrics-cell-bars'} style={barStyle}>
          <span style={textContainerStyle}>{_.isNumber(count) ? L10n.localizedCount(count) : ''}</span>
        </div>
      );
    };
  }

  export function AlertCell(type: string, translate: IReactTranslateService,
    angularContext: AngularContext, testId?: string): React.StatelessComponent<BodyCell.Props> {
    return function AlertCell(props: BodyCell.Props) {
      const hasAlert = props.row.data['hasAlert'];
      if (!hasAlert) {
        return null;
      }

      const id = props.row.data['id'];

      return DeprecatedReactMenu.element({
        tbTestId: testId,
        menuButtonFactory: () => <span className="tb-data-grid-icon tb-icon-alert"
          data-tb-test-id={testId}
          title={translate('hasAlert')} />,
        menuPanelFactory: () => {

          // TODO: rewrite tb:alert-info as a React component
          return AngularDirective.element(
            {
              angularContext: angularContext,
              template: "<div class='tb-popup-menu-content tb-padded-small' style='min-width: 250px; white-space: normal; line-height: normal'>" +
                "  <tb:alert-info type=\"'" + type + "'\" id=\"'" + id + "'\"></tb:alert-info>" +
                '</div>'
            }
          );

        }
      });
    };
  }

  export function ScheduleDescriptionCell(
    scheduleUrlFn: ((row: DataGrid.Row) => string) | null,
    scheduleFrequencyDescriptionFilter: IScheduleFrequencyDescriptionFilter): React.StatelessComponent<BodyCell.Props> {
    return StyledCellFormatter(
      function ScheduleDescriptionCell(props: BodyCell.Props) {
        const row = props.row;
        const schedule = row.data.schedule as ISchedule;

        const scheduleDescription = scheduleFrequencyDescriptionFilter(schedule);

        if (schedule.definedBy === DefinedBy.SELF_SERVICE) {
          //Self Service Schedule: only show the description
          return (
            <span title={scheduleDescription}>{scheduleDescription}</span>
          );
        } else {
          return (
            <span>
              {scheduleUrlFn
                ? <a href={scheduleUrlFn(row)} title={schedule.name}>{schedule.name}</a>
                : <span title={schedule.name}>{schedule.name}</span>
              }
              <span>&nbsp;&ndash;&nbsp;</span>
              <span title={scheduleDescription}>{scheduleDescription}</span>
            </span>
          );
        }
      }
    );
  }

  export function extractTaskStatus() {
    return StyledCellFormatter(
      function extractTaskStatus(props: BodyCell.Props) {
        const state = props.row.data.state;
        return <span>{ state }</span>;
      }
    );
  }

  export function ScheduleDescriptionCellWithIcon(
    subscriptionConditionFn: (row: DataGrid.Row) => boolean,
    translate: IReactTranslateService,
    scheduleUrlFn: (row: DataGrid.Row) => string,
    scheduleFrequencyDescriptionFilter: IScheduleFrequencyDescriptionFilter,
    subscriptionOnExtractRefreshFn: (row: DataGrid.Row) => boolean): React.StatelessComponent<BodyCell.Props> {
    return StyledCellFormatter(
      function ScheduleDescriptionCell(props: BodyCell.Props) {

        const row = props.row;
        const schedule = row.data.schedule as ISchedule;

        let cellStyle = {};
        let textStyles = {};
        let Icon: () => JSX.Element | undefined = undefined;

        if (subscriptionConditionFn(row)) {
          cellStyle = _.extend({ cursor: 'default' }, CommonStyles.Layout.FlexBlockLeftAndCenter);
          textStyles = _.extend({ display: 'block', flexShrink: 1 }, CommonStyles.Layout.TextEllipsis);

          const iconStyle = {
            backgroundImage: imgUrl('help_header_icon_dark.svg'),
            height: '35px',
            width: '35px',
            backgroundPosition: 'left center',
            backgroundRepeat: 'no-repeat',
            marginLeft: '5px',
            flexShrink: 0
          };

          Icon = () => <span data-tb-test-id="data-grid-cell-icon"
            style={iconStyle}
            className={'data-present-class'}
            title={translate('column_subscriptionDataPresent')} />;
        }

        if(subscriptionOnExtractRefreshFn(row)) {
          return (
            <span style={cellStyle}>
            <span style={textStyles}>
              <span title={translate('subscriptionOnExtractRefreshSchedule')}>{translate('subscriptionOnExtractRefreshSchedule')}</span>
            </span>
            {Icon && <Icon />}
          </span>
          );
        } else {
          const scheduleDescription = scheduleFrequencyDescriptionFilter(schedule);
          const ScheduleLink = () => (
            <a href={scheduleUrlFn(row)}
              title={schedule.name}>
              {schedule.name}
            </a>
          );

          const Dash = () => <span>&nbsp;&ndash;&nbsp;</span>;

          const selfService = schedule.definedBy === DefinedBy.SELF_SERVICE;
          //Exclude the dash and the schedule link if the schedule was created by self service

          return (
            <span style={cellStyle}>
              <span style={textStyles}>
                {selfService ? null : ScheduleLink()}
                {selfService ? null : Dash()}
                <span title={scheduleDescription}>{scheduleDescription}</span>
              </span>
              {Icon && <Icon />}
            </span>
          );
        }
      }
    );
  }


  export function SiteRolePopoverCell(angularContext: AngularContext): React.Factory<any> {
    return React.createFactory(class extends React.Component<HeaderColumn.HeaderFormatterProps, any> {
      render() {
        return <SiteRolePopover angularContext={angularContext} />;
      }
    });
  }

  export function SiteGridCheckboxCell(checkboxProps: (site: ISiteNameWithId) => ReactCheckboxTristate.Props): React.StatelessComponent<BodyCell.Props> {
    return StyledCellFormatter(
      function SiteGridCheckboxCell(props: BodyCell.Props) {
        const row = props.row;
        let checkboxClass = 'tb-checkbox-site-grid-cell';

        return (<span className={checkboxClass}>
          <ReactCheckboxTristate {...checkboxProps(row.data)} />
        </span>);
      }
    );
  }

  export function SiteGridDropdownCell(dropdownProps: (site: ISiteNameWithId) => SiteRolePickerMenu.Props,
    checkboxProps: (site: ISiteNameWithId) => ReactCheckboxTristate.Props): React.Factory<BodyCell.Props> {
    return StyledCellFormatter(
      function SiteGridDropdownCell(props: BodyCell.Props) {
        const row = props.row;
        let dropdownClass = 'tb-server-user-site-role-dropdown-menu ';
        if (checkboxProps(row.data)) {
          dropdownClass += (checkboxProps(row.data).checkboxState === 'empty') ? 'tb-hidden' : '';
        }
        return (<div className={dropdownClass}>
          <SiteRolePickerMenu {...dropdownProps(row.data)} />
        </div>);
      }
    );
  }

  export function IconHeader(onIconClicked: () => void, iconClass: string): React.Factory<HeaderColumn.HeaderFormatterProps> {
    const headerRoot = cssClass('iconHeaderCell', 'root', {
      display: 'flex',
      alignItems: 'center',
      height: '100%'
    });
    const headerButton = cssClass('iconHeaderCell', 'button', {
      display: 'flex',
      alignItems: 'center',
      height: 20,
      marginLeft: TabStyles.Spacing.MediumSpace,
      marginRight: TabStyles.Spacing.MediumSpace,
      padding: `${TabStyles.Spacing.SmallSpace}px ${TabStyles.Spacing.DefaultSpace}px`,
      cursor: 'pointer',
      $nest: {
        '&:hover': {
          backgroundColor: CommonStyles.Colors.Grey230
        }
      }
    });
    return React.createFactory(class extends React.Component<HeaderColumn.HeaderFormatterProps, any> {
      render() {
        return (
          <div
            className={headerRoot}
            onClick={onIconClicked}
            title={this.props.col.headerText}
          >
            <div className={headerButton}>
              <span className={iconClass} />
              <span>{this.props.col.headerText}</span>
            </div>
          </div>
        );
      }
    });
  }

  export interface IconCellOptions {
    onIconClicked: (row: DataGrid.Row) => void;
    iconClass: string;
    title: string;
    label?: string;
  }

  export function IconCell(options: IconCellOptions): React.Factory<BodyCell.Props> {
    const rootClass = cssClass('iconCell', 'root', {
      cursor: 'pointer',
      padding: CommonStyles.Sizes.DefaultSpace,
      $nest: {
        '&:hover': {
          backgroundColor: CommonStyles.Colors.Grey230
        }
      }
    });
    return StyledCellFormatter(
      React.createFactory(class extends React.Component<BodyCell.Props, any> {
        displayName: 'IconCell';

        boundHandleClick = this.handleClick.bind(this);
        handleClick(event: React.MouseEvent<HTMLSpanElement>) {
          const props: BodyCell.Props = this.props;
          options.onIconClicked(props.row);
          event.preventDefault();
          event.stopPropagation();
        }
        render() {
          const props: BodyCell.Props = this.props;

          return (
            <span className={rootClass} onClick={this.boundHandleClick}>
              <span className={options.iconClass} title={options.title} />
              {options.label ? <span>{options.label}</span> : null}
            </span>
          );
        }
      })
    );
  }

  export function CheckboxHeaderWithText(isCheckboxHeaderChecked: boolean, onCheckboxHeaderClicked: () => void, isDisabled?: boolean): React.Factory<HeaderColumn.HeaderFormatterProps> {
    const headerRoot = cssClass('checkboxHeaderCell', 'root', textEllipsis, {
      paddingLeft: TabStyles.Spacing.MediumSpace,
      paddingRight: TabStyles.Spacing.MediumSpace
    });
    return React.createFactory(class extends React.Component<HeaderColumn.HeaderFormatterProps, any> {
      render() {
        return (
          <div className={headerRoot}>
            <CheckBoxWidget
              checked={isCheckboxHeaderChecked}
              testId={'checkbox-header'}
              handleChange={onCheckboxHeaderClicked}
              disabled={isDisabled}
            />
            <span title={this.props.col.headerText}>{this.props.col.headerText}</span>
          </div>
        );
      }
    });
  }

  export function CheckboxCellWithText(propertyName: string, isCheckboxChecked: (rowKey: string) => boolean, onCheckboxClicked: (row: DataGrid.Row) => void, isDisabled?: boolean): React.Factory<BodyCell.Props> {
    const textOverflow = cssClass('checkboxCell', 'textOverflow', textEllipsis);
    return StyledCellFormatter(
      React.createFactory(class extends React.Component<BodyCell.Props, any> {
        displayName: 'CheckboxCellWithText';

        boundHandleClick = this.handleClick.bind(this);
        handleClick(event: React.MouseEvent<HTMLSpanElement>) {
          const props: BodyCell.Props = this.props;
          onCheckboxClicked(props.row);
          event.preventDefault();
          event.stopPropagation();
        }
        render() {
          const text = this.props.row.data[propertyName];
          return (
            <div className={textOverflow}>
              <CheckBoxWidget
                checked={isCheckboxChecked(this.props.row.key)}
                testId={'checkbox-' + text}
                handleChange={this.boundHandleClick}
                disabled={isDisabled}
              />
              <span title={text}>{text}</span>
            </div>
          );
        }
      })
    );
  }
}
